// Search across chats, on this device (M25, docs/search-and-mentions.md).
// Messages are copied into IndexedDB as they arrive and by a background
// backfill of each chat's history, then searched here. Nothing is sent to the
// server, so it keeps working once message content is end-to-end encrypted
// (M28), and it works offline. One database per user, deleted on logout (D44).
// Each message's words are sealed with this device's key (D281, #84); only
// what the server knows anyway (chat, family, time) is stored readable, for
// lookups and clearing. Opened entries are kept in memory while the page is.
import { isExpired } from '@twinden/shared';
import { api } from './api';
import { openText, sealText } from './e2ee/rest';

export interface IndexedMessage {
  id: string;
  conversationId: string;
  familyId: string;
  senderId: string | null;
  senderName: string;
  type: string;
  /** What's searchable: the text, or a file's name. */
  text: string;
  createdAt: string;
  /** A disappearing message's time (D238): not found after it. */
  expiresAt?: string | null;
}

/** The parts of a message the index needs. */
export interface IndexableMessage {
  id: string;
  conversationId: string;
  sender?: { id: string; displayName: string } | null;
  type: string;
  content: string;
  metadata?: Record<string, unknown> | null;
  deletedAt?: string | null;
  createdAt: string;
  expiresAt?: string | null;
  /** An opened poll's options (M60, D223). */
  e2ee?: { envelope?: { poll?: { options?: string[] } } };
}

// ---- Matching (pure) -------------------------------------------------------

/** Lowercase, without accents, so "cafe" finds "Café". Other scripts pass through. */
export function normalize(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function queryTerms(query: string): string[] {
  return normalize(query).split(/\s+/).filter(Boolean);
}

/** Every word of the query appears somewhere in the message (or its sender). */
export function matches(entry: IndexedMessage, terms: string[]): boolean {
  if (terms.length === 0) return false;
  const haystack = normalize(`${entry.text}\n${entry.senderName}`);
  return terms.every((term) => haystack.includes(term));
}

export function searchEntries(
  entries: IndexedMessage[],
  query: string,
  limit = 50
): IndexedMessage[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return [];
  const now = Date.now();
  return entries
    .filter(
      (entry) => !isExpired(entry.expiresAt, now) && matches(entry, terms)
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, limit);
}

/**
 * The bit of the text around the first match, split for highlighting:
 * "…we land at" + "Heathrow" + " at 6 on Friday".
 */
export function snippet(
  text: string,
  query: string,
  radius = 40
): { before: string; match: string; after: string } {
  const terms = queryTerms(query);
  const flat = text.replace(/\s+/g, ' ').trim();
  const lower = normalize(flat);
  let index = -1;
  let length = 0;
  for (const term of terms) {
    const found = lower.indexOf(term);
    if (found !== -1 && (index === -1 || found < index)) {
      index = found;
      length = term.length;
    }
  }
  if (index === -1) {
    return { before: '', match: '', after: flat.slice(0, radius * 2) };
  }
  const start = Math.max(0, index - radius);
  const end = Math.min(flat.length, index + length + radius);
  return {
    before: (start > 0 ? '…' : '') + flat.slice(start, index),
    match: flat.slice(index, index + length),
    after: flat.slice(index + length, end) + (end < flat.length ? '…' : ''),
  };
}

/** What gets indexed for a message, or null if nothing (photos, deleted…). */
export function toIndexed(
  message: IndexableMessage,
  familyId: string
): IndexedMessage | null {
  if (message.deletedAt || isExpired(message.expiresAt)) return null;
  const fileName =
    typeof message.metadata?.fileName === 'string'
      ? message.metadata.fileName
      : '';
  // An album's caption and a poll's question with its options are found
  // too (D223); both are opened on the device first.
  const options = message.e2ee?.envelope?.poll?.options ?? [];
  const text =
    message.type === 'text' || message.type === 'album'
      ? message.content
      : message.type === 'poll'
        ? [message.content, ...options].join('\n')
        : // A gathering is found by its title (M71, D282).
          message.type === 'gathering'
          ? message.content
          : message.type === 'file'
            ? fileName
            : '';
  if (!text.trim()) return null;
  return {
    id: message.id,
    conversationId: message.conversationId,
    familyId,
    senderId: message.sender?.id ?? null,
    senderName: message.sender?.displayName ?? '',
    type: message.type,
    text,
    createdAt: message.createdAt,
    ...(message.expiresAt ? { expiresAt: message.expiresAt } : {}),
  };
}

// ---- Storage (IndexedDB) ---------------------------------------------------

/** A message as stored: its words sealed (D281). */
interface StoredEntry {
  id: string;
  conversationId: string;
  familyId: string;
  createdAt: string;
  expiresAt?: string | null;
  /** `{ senderId, senderName, type, text }`, sealed with the device's key. */
  sealed: string;
}

type SealedPart = Pick<
  IndexedMessage,
  'senderId' | 'senderName' | 'type' | 'text'
>;

// Opened entries, by id, with the sealed value they came from.
const opened = new Map<string, { sealed: string; entry: IndexedMessage }>();

async function sealEntry(
  userId: string,
  entry: IndexedMessage
): Promise<StoredEntry | null> {
  const part: SealedPart = {
    senderId: entry.senderId,
    senderName: entry.senderName,
    type: entry.type,
    text: entry.text,
  };
  const sealed = await sealText(userId, JSON.stringify(part));
  if (!sealed) return null;
  opened.set(entry.id, { sealed, entry });
  return {
    id: entry.id,
    conversationId: entry.conversationId,
    familyId: entry.familyId,
    createdAt: entry.createdAt,
    ...(entry.expiresAt ? { expiresAt: entry.expiresAt } : {}),
    sealed,
  };
}

async function openEntry(
  userId: string,
  stored: StoredEntry
): Promise<IndexedMessage | null> {
  const known = opened.get(stored.id);
  if (known?.sealed === stored.sealed) return known.entry;
  const text = await openText(userId, stored.sealed);
  if (!text) return null;
  try {
    const part = JSON.parse(text) as SealedPart;
    const entry: IndexedMessage = {
      id: stored.id,
      conversationId: stored.conversationId,
      familyId: stored.familyId,
      createdAt: stored.createdAt,
      ...(stored.expiresAt ? { expiresAt: stored.expiresAt } : {}),
      senderId: part.senderId,
      senderName: part.senderName,
      type: part.type,
      text: part.text,
    };
    opened.set(stored.id, { sealed: stored.sealed, entry });
    return entry;
  } catch {
    return null;
  }
}

async function openEntries(
  userId: string,
  stored: StoredEntry[]
): Promise<IndexedMessage[]> {
  const entries = await Promise.all(stored.map((s) => openEntry(userId, s)));
  return entries.filter((entry): entry is IndexedMessage => entry !== null);
}

const DB_PREFIX = 'twinden-search-';
const MESSAGES = 'messages';
const PROGRESS = 'progress';

interface Progress {
  conversationId: string;
  /** Oldest message reached so far; history before it is still to fetch. */
  cursor?: string;
  /** The whole history has been indexed. */
  complete: boolean;
}

let dbPromise: Promise<IDBDatabase> | null = null;
let dbUser: string | null = null;

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Opens (or switches to) this user's index; null closes it. */
export function openIndexFor(userId: string | null) {
  if (userId === dbUser) return;
  void dbPromise?.then((db) => db.close()).catch(() => undefined);
  dbPromise = null;
  opened.clear();
  dbUser = userId;
  if (!userId || typeof indexedDB === 'undefined') return;
  dbPromise = new Promise((resolve, reject) => {
    // Version 2 seals the words (D281): version 1's plain entries are
    // dropped, with the backfill's progress, so history is indexed again.
    const open = indexedDB.open(DB_PREFIX + userId, 2);
    open.onupgradeneeded = () => {
      const db = open.result;
      for (const name of [MESSAGES, PROGRESS]) {
        if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name);
      }
      const store = db.createObjectStore(MESSAGES, { keyPath: 'id' });
      store.createIndex('conversationId', 'conversationId');
      db.createObjectStore(PROGRESS, { keyPath: 'conversationId' });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
  dbPromise.catch(() => {
    dbPromise = null;
  });
}

/** Signing out forgets the index on this device (D44). */
export function deleteIndex(userId: string) {
  if (typeof indexedDB === 'undefined') return;
  if (dbUser === userId) openIndexFor(null);
  indexedDB.deleteDatabase(DB_PREFIX + userId);
}

async function database(): Promise<IDBDatabase | null> {
  try {
    return dbPromise ? await dbPromise : null;
  } catch {
    return null;
  }
}

/** Adds or refreshes messages; deleted ones (and ones with no text) are removed. */
export async function indexMessages(
  messages: IndexableMessage[],
  familyId: string
) {
  const db = await database();
  const userId = dbUser;
  if (!db || !userId || messages.length === 0) return;
  // Sealed first: a transaction can't wait for the crypto.
  const sealed = await Promise.all(
    messages.map(async (message) => {
      const entry = toIndexed(message, familyId);
      return {
        id: message.id,
        stored: entry ? await sealEntry(userId, entry) : null,
      };
    })
  );
  const tx = db.transaction(MESSAGES, 'readwrite');
  const store = tx.objectStore(MESSAGES);
  for (const { id, stored } of sealed) {
    if (stored) {
      store.put(stored);
    } else {
      store.delete(id);
      opened.delete(id);
    }
  }
  await done(tx).catch(() => undefined);
}

/**
 * An edit or delete of a message we may have indexed: refreshed in place,
 * keeping the family it was indexed under (edit payloads don't carry it).
 */
export async function reindexMessage(message: IndexableMessage) {
  const db = await database();
  const userId = dbUser;
  if (!db || !userId) return;
  const existing = (await request(
    db.transaction(MESSAGES, 'readonly').objectStore(MESSAGES).get(message.id)
  )) as StoredEntry | undefined;
  if (!existing) return;
  const entry = toIndexed(message, existing.familyId);
  const stored = entry ? await sealEntry(userId, entry) : null;
  const tx = db.transaction(MESSAGES, 'readwrite');
  if (stored) {
    tx.objectStore(MESSAGES).put(stored);
  } else {
    tx.objectStore(MESSAGES).delete(message.id);
    opened.delete(message.id);
  }
  await done(tx).catch(() => undefined);
}

export async function unindexMessage(id: string) {
  const db = await database();
  if (!db) return;
  const tx = db.transaction(MESSAGES, 'readwrite');
  tx.objectStore(MESSAGES).delete(id);
  opened.delete(id);
  await done(tx).catch(() => undefined);
}

/** Searches this family's chats on the device, newest first. */
export async function searchIndex(
  familyId: string,
  query: string,
  limit = 50
): Promise<IndexedMessage[]> {
  const db = await database();
  const userId = dbUser;
  if (!db || !userId || queryTerms(query).length === 0) return [];
  const stored: StoredEntry[] = [];
  const tx = db.transaction(MESSAGES, 'readonly');
  await new Promise<void>((resolve, reject) => {
    const cursor = tx.objectStore(MESSAGES).openCursor();
    cursor.onsuccess = () => {
      const current = cursor.result;
      if (!current) return resolve();
      const entry = current.value as StoredEntry;
      if (entry.familyId === familyId && !isExpired(entry.expiresAt))
        stored.push(entry);
      current.continue();
    };
    cursor.onerror = () => reject(cursor.error);
  });
  return searchEntries(await openEntries(userId, stored), query, limit);
}

/**
 * A family was deleted (M54, D191): everything indexed from its chats goes
 * from this device, with each chat's backfill progress.
 */
export async function forgetFamilyIndex(
  familyId: string,
  conversationIds: string[]
) {
  const db = await database();
  if (!db) return;
  const tx = db.transaction([MESSAGES, PROGRESS], 'readwrite');
  const messages = tx.objectStore(MESSAGES);
  const chats = new Set(conversationIds);
  const cursor = messages.openCursor();
  cursor.onsuccess = () => {
    const current = cursor.result;
    if (!current) return;
    const entry = current.value as StoredEntry;
    if (entry.familyId === familyId || chats.has(entry.conversationId)) {
      current.delete();
      opened.delete(entry.id);
    }
    current.continue();
  };
  for (const id of conversationIds) tx.objectStore(PROGRESS).delete(id);
  await done(tx).catch(() => undefined);
}

/**
 * Disappearing messages leave the device's index (D238): the ones named
 * (the server swept them), and any whose time is up.
 */
export async function forgetExpired(messageIds: string[] = []) {
  const db = await database();
  if (!db) return;
  const tx = db.transaction(MESSAGES, 'readwrite');
  const store = tx.objectStore(MESSAGES);
  for (const id of messageIds) store.delete(id);
  const now = Date.now();
  const cursor = store.openCursor();
  cursor.onsuccess = () => {
    const current = cursor.result;
    if (!current) return;
    if (isExpired((current.value as StoredEntry).expiresAt, now))
      current.delete();
    current.continue();
  };
  await done(tx).catch(() => undefined);
}

/** Index a chat's history again (its keys just arrived, M28). */
export async function forgetProgress(conversationId: string) {
  const db = await database();
  if (!db) return;
  const tx = db.transaction(PROGRESS, 'readwrite');
  tx.objectStore(PROGRESS).delete(conversationId);
  await done(tx).catch(() => undefined);
}

/** Searches one chat on the device, newest first (M28: encrypted chats). */
export async function searchConversation(
  conversationId: string,
  query: string,
  limit = 50
): Promise<IndexedMessage[]> {
  const db = await database();
  const userId = dbUser;
  const terms = queryTerms(query);
  if (!db || !userId || terms.length === 0) return [];
  const tx = db.transaction(MESSAGES, 'readonly');
  const stored = (await request(
    tx.objectStore(MESSAGES).index('conversationId').getAll(conversationId)
  )) as StoredEntry[];
  return searchEntries(await openEntries(userId, stored), query, limit);
}

async function knownIds(db: IDBDatabase, ids: string[]): Promise<Set<string>> {
  const tx = db.transaction(MESSAGES, 'readonly');
  const store = tx.objectStore(MESSAGES);
  const found = await Promise.all(ids.map((id) => request(store.getKey(id))));
  return new Set(
    found.filter((key): key is IDBValidKey => key !== undefined).map(String)
  );
}

async function getProgress(
  db: IDBDatabase,
  conversationId: string
): Promise<Progress> {
  const tx = db.transaction(PROGRESS, 'readonly');
  const row = (await request(tx.objectStore(PROGRESS).get(conversationId))) as
    | Progress
    | undefined;
  return row ?? { conversationId, complete: false };
}

async function setProgress(db: IDBDatabase, progress: Progress) {
  const tx = db.transaction(PROGRESS, 'readwrite');
  tx.objectStore(PROGRESS).put(progress);
  await done(tx).catch(() => undefined);
}

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Brings the index up to date for these chats, gently (one page at a time,
 * with a pause between). First catches up on what's new since last time, then
 * keeps walking back through history until each chat is complete.
 */
export async function backfill(
  familyId: string,
  conversationIds: string[],
  signal: AbortSignal
) {
  for (const conversationId of conversationIds) {
    if (signal.aborted) return;
    const db = await database();
    if (!db) return;
    try {
      const progress = await getProgress(db, conversationId);

      // Newest first: the first time, one page to start from; after that,
      // page back until we meet messages we already have.
      let cursor: string | undefined;
      for (;;) {
        if (signal.aborted) return;
        const page = await api.getMessages(conversationId, cursor);
        const known = await knownIds(
          db,
          page.data.map((message) => message.id)
        );
        await indexMessages(page.data, familyId);
        if (!progress.cursor && !progress.complete) {
          progress.cursor = page.pagination.cursor;
          progress.complete = !page.pagination.hasMore;
          await setProgress(db, progress);
          break;
        }
        if (known.size > 0 || !page.pagination.hasMore) break;
        cursor = page.pagination.cursor;
        await pause(250);
      }

      // Then older history, from where the last run stopped.
      let older = progress.cursor;
      while (!progress.complete && older) {
        if (signal.aborted) return;
        await pause(250);
        const page = await api.getMessages(conversationId, older);
        await indexMessages(page.data, familyId);
        older = page.pagination.cursor;
        progress.complete = !page.pagination.hasMore;
        await setProgress(db, {
          conversationId,
          cursor: older,
          complete: progress.complete,
        });
      }
    } catch {
      // Offline or the chat went away: try again next time.
    }
  }
}
